import { memo, useEffect, useRef } from 'react';
import { Element, LazyloadImage, RouterLink } from 'src/components';
import { IBaseSection } from 'src/interface';
import cn from 'classnames';
import { IMatch, MATCH_STATUS } from 'src/modules/soccer/interface';
import styles from './styles.module.scss';

interface IProps extends IBaseSection<IMatch> {}

function ScoreItem({
    id,
    link,
    competition,
    date,
    time,
    home,
    away,
    matchStatus,
}: IMatch) {
    const notStarted =
        matchStatus == MATCH_STATUS.WAITING ||
        matchStatus == MATCH_STATUS.CANCEL;
    const isLive = matchStatus == MATCH_STATUS.LIVE;

    return (
        <RouterLink
            to={link}
            className={cn(
                styles['score-item'],
                styles[`status-${matchStatus}`]
            )}
        >
            <Element className={styles['header']}>
                <Element renderAs="span" className={styles['name']}>
                    {competition.name}
                </Element>
                <Element renderAs="span" className={styles['datetime']}>
                    {notStarted && `${time} -  ${date}`}
                    {isLive && `Trực tiếp - ${time}`}
                    {!notStarted && !isLive && date}
                </Element>
            </Element>
            <Element className={styles['content']}>
                <Element className={cn(styles['team'], styles['team--a'])}>
                    <Element className={styles['team-wrapper']}>
                        <LazyloadImage src={home.team.logo} />
                        <Element className={styles['name']}>
                            {home.team.name}
                        </Element>
                    </Element>
                    <Element
                        className={cn(styles['score'], {
                            [styles['lost']]: home.scored < away.scored,
                        })}
                    >
                        {matchStatus == MATCH_STATUS.WAITING ||
                        matchStatus == MATCH_STATUS.CANCEL
                            ? '-'
                            : home.scored}
                        {!!home.scoredNote && <span>({home.scoredNote})</span>}
                    </Element>
                </Element>
                <Element className={cn(styles['team'], styles['team--b'])}>
                    <Element className={styles['team-wrapper']}>
                        <LazyloadImage src={away.team.logo} />
                        <Element className={styles['name']}>
                            {away.team.name}
                        </Element>
                    </Element>
                    <Element
                        className={cn(styles['score'], {
                            [styles['lost']]: away.scored < home.scored,
                        })}
                    >
                        {matchStatus == MATCH_STATUS.WAITING ||
                        matchStatus == MATCH_STATUS.CANCEL
                            ? '-'
                            : away.scored}
                        {!!away.scoredNote && <span>({away.scoredNote})</span>}
                    </Element>
                </Element>
            </Element>
        </RouterLink>
    );
}

export function ScoreSection(props: IProps) {
    const { items = [] } = props;
    const ref = useRef();
    const todayRef = useRef<HTMLDivElement>(null);
    const setTodayRef = useRef<boolean>(false);

    useEffect(() => {
        if (!todayRef.current) return;

        if (ref && ref.current) {
            (ref.current as any).scrollLeft =
                todayRef.current.getBoundingClientRect().left - 20;
        }
    }, [todayRef]);

    return (
        <Element className={styles['score-section']}>
            <Element className={styles['score-wrapper']} domRef={ref}>
                {items.map((item, i) => {
                    const isLive = item.matchStatus == MATCH_STATUS.LIVE;
                    const isWaiting = item.matchStatus == MATCH_STATUS.WAITING;
                    if ((isLive || isWaiting) && !setTodayRef.current) {
                        setTodayRef.current = true;
                        return (
                            <Element domRef={todayRef} key={i}>
                                <ScoreItem key={i} {...item} />
                            </Element>
                        );
                    }
                    return (
                        <Element key={i}>
                            <ScoreItem key={i} {...item} />
                        </Element>
                    );
                })}
            </Element>
        </Element>
    );
}

export default memo(ScoreSection);
